'use client';

import { useMemo, useState, useEffect, useCallback } from 'react';
import { useAppStore } from '@/stores/app-store';
import { t } from '@/lib/i18n';
import { getAIInsights, ROOM } from '@/lib/ai-engine';
import { resolveReportDomain } from '@/lib/ai-report-prompts';
import { hasPerm } from '@/lib/permissions';
import { isAddonEnabled } from '@/lib/entitlements';
import { AIInsights } from '@/lib/types';
import StatCard from '@/components/StatCard';
import AddonLocked from '@/components/AddonLocked';

export default function AIInsightsPage() {
  const currentUser = useAppStore((s) => s.currentUser);
  const currentPlant = useAppStore((s) => s.currentPlant);
  const plants = useAppStore((s) => s.plants);
  const lang = useAppStore((s) => s.lang);
  const dbUser = useAppStore((s) => s.dbUser);
  const [dbInsights, setDbInsights] = useState<AIInsights | null>(null);
  const [loading, setLoading] = useState(false);
  const [guideOpen, setGuideOpen] = useState(true);

  const fetchInsights = useCallback(async () => {
    if (!currentPlant || !dbUser) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/projects/${currentPlant}/insights`);
      if (res.ok) {
        setDbInsights(await res.json());
      }
    } catch {
      // fall back to mock
    } finally {
      setLoading(false);
    }
  }, [currentPlant, dbUser]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  // AI Insights is a paid add-on — not included in the current package.
  if (!isAddonEnabled('ai')) {
    return (
      <AddonLocked
        titleTh="AI · วิเคราะห์และตรวจจับความผิดปกติ"
        titleEn="AI · Insight & Anomaly Detection"
        descTh="ไม่ต้องเปิดดูทีละไซต์เพื่อหาว่ามีอะไรผิด — ระบบไล่ดูให้ทุกไซต์แล้วบอกว่าควรไปที่ไหนก่อน พร้อมเหตุผลที่ตรวจสอบย้อนได้"
        descEn="Stop opening one site after another to find the problem — it reviews them all and tells you which one needs you first, with the reasoning shown."
        priceTh="มีในแพ็กเกจ Starter ขึ้นไป"
        priceEn="Included from Starter upward"
      />
    );
  }

  if (!hasPerm(currentUser, 'ai')) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted, #94a3b8)', fontSize: 14 }}>
        {t('accessDenied', lang)}
      </div>
    );
  }

  if (!currentPlant) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted, #94a3b8)', fontSize: 14 }}>
        {t('selectPlant', lang)}
      </div>
    );
  }

  const plant = plants[currentPlant];
  /*
   * ── ชนิดของระบบมาจากฝั่งเซิร์ฟเวอร์ ────────────────────────────────────
   * หน้านี้เคยเดาชนิดไซต์เอง ด้วยเงื่อนไขคนละชุดกับกฎที่เซิร์ฟเวอร์ใช้จริง
   * ไซต์ที่ไม่เข้าเงื่อนไขไหนเลยจึงตกไปที่ "ตู้แช่วัคซีน" — ไซต์เครื่องปั่นไฟ
   * อ่านแล้วเจอคำอธิบายเกณฑ์ 2–8°C ตาม WHO EPI กับคำแนะนำให้ไปตรวจซีลประตู
   *
   * ตอนนี้ /insights ส่ง domain ที่มันใช้ตัดสินกฎมาด้วย หน้าเว็บจึงอธิบาย
   * ตรงกับสิ่งที่ระบบทำจริงเสมอ (resolveReportDomain ใช้ตอนยังไม่ได้ข้อมูล
   * หรือโหมดเดโมที่ไม่มีเซิร์ฟเวอร์ตอบ)
   */
  const domain = dbInsights?.domain ?? resolveReportDomain({
    plantType: plant?.type,
    solution: plant?.solution,
  });
  const isWater = domain === 'water' || domain === 'wwtp';
  // Effluent (WWTP) sites have a water-quality sensor (ph/cod/bod); supply/tank
  // sites (pumps + water level, e.g. TWE) do not — they get a pump+level guide.
  const hasEffluent = (plant?.devices || []).some((d) => ['ph', 'cod', 'bod'].some((k) => k in (d.fieldNames || {})));
  const isSupply = isWater && !hasEffluent;
  const isEnergy = domain === 'energy';
  // ห้องเครื่องมือแพทย์ — เกณฑ์เดียวกับฝั่งเซิร์ฟเวอร์ (insights route) และ
  // กับ lib/ai-engine.ts เพื่อไม่ให้หน้าไซต์กับหน้าทุกไซต์บอกคนละเรื่อง
  const isMedical = domain === 'medical';
  // ตู้แช่วัคซีนจริงเท่านั้นที่ได้เกณฑ์ 2–8°C — ไม่ใช่ "ทุกไซต์ที่เหลือ"
  const isCold = domain === 'coldchain';
  const isGenset = domain === 'smartgen';
  // ฟาร์มไก่เนื้อ — กฎจาก lib/poultry.ts (เกณฑ์ตามอายุไก่ + ดัชนีความร้อน)
  const isPoultry = domain === 'poultry';
  // Use DB insights for DB users, mock for demo users
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const mockInsights = useMemo(() => (!dbUser ? getAIInsights(plant) : null), [plant, dbUser]);
  const insights = dbInsights || mockInsights || { warnings: [], anomalies: [], predictions: [] };

  const criticals = insights.warnings.filter((w) => w.sv === 'critical').length;
  const warns = insights.warnings.filter((w) => w.sv === 'warning').length;
  const anomCount = insights.anomalies.length;
  const healthScore = Math.max(0, 100 - criticals * 20 - warns * 10 - anomCount * 15);

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
        <div style={{ width: 36, height: 36, border: '3px solid var(--glass-border)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
        <div style={{ fontSize: 13 }}>Analyzing devices...</div>
      </div>
    );
  }

  const panelStyle: React.CSSProperties = {
    background: 'var(--bg-surface)',
    borderRadius: 16,
    border: '1px solid var(--glass-border)',
    overflow: 'hidden',
    marginBottom: 20,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
  };

  const headStyle: React.CSSProperties = {
    padding: '14px 20px',
    fontSize: 15,
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border-subtle)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };

  const bodyStyle: React.CSSProperties = {
    padding: 20,
  };

  const sevDotColor = (sv: string) => {
    if (sv === 'critical') return '#DC2626';
    if (sv === 'warning') return '#F5A623';
    return '#3B82F6';
  };

  const sevBadge = (sv: string) => {
    const colors: Record<string, { bg: string; text: string; label: string }> = {
      critical: { bg: 'rgba(220,38,38,0.15)', text: '#DC2626', label: 'CRITICAL' },
      warning: { bg: 'rgba(251,191,36,0.15)', text: '#F59E0B', label: 'WARNING' },
      info: { bg: 'rgba(59,130,246,0.15)', text: '#3B82F6', label: 'INFO' },
    };
    const c = colors[sv] || colors.info;
    return (
      <span style={{
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: 4,
        background: c.bg,
        color: c.text,
        textTransform: 'uppercase',
        letterSpacing: '.04em',
      }}>
        {c.label}
      </span>
    );
  };

  /* Icons for stat cards */
  const heartIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12h3l2-4 3 8 2-4h3" />
      <rect x="2" y="6" width="20" height="12" rx="3" />
      <circle cx="19" cy="9" r="1" fill="#fff" stroke="none" />
    </svg>
  );
  const alertIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3L2 20h20L12 3z" />
      <path d="M12 3v2M2 20h20" />
      <line x1="12" y1="10" x2="12" y2="14" />
      <circle cx="12" cy="17" r="0.5" fill="#fff" stroke="none" />
      <path d="M7.5 17h-2M18.5 17h-2" />
    </svg>
  );
  const anomalyIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      <circle cx="12" cy="12" r="9" strokeDasharray="3 2" />
      <circle cx="15" cy="6" r="1" fill="#fff" stroke="none" />
      <circle cx="9" cy="18" r="1" fill="#fff" stroke="none" />
    </svg>
  );
  const predIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 17 8 12 12 15 17 8 21 4" />
      <polyline points="17 4 21 4 21 8" />
      <path d="M3 21h18" />
      <path d="M17 8l-1 9M12 15v6M8 12v9" strokeDasharray="2 1.5" />
    </svg>
  );
  const lightbulbIcon = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a7 7 0 0 0-4 12.73V17h8v-2.27A7 7 0 0 0 12 2z" />
      <line x1="9" y1="20" x2="15" y2="20" />
      <line x1="10" y1="23" x2="14" y2="23" />
      <path d="M12 6v4M10 8h4" />
      <circle cx="18" cy="5" r="1" fill="#3B82F6" stroke="none" />
      <path d="M18 5l2-2" />
    </svg>
  );

  /* Section header icons */
  const warningTriangleIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
  const anomalyWaveIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
  const trendChartIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );
  const circuitIcon = (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" />
    </svg>
  );
  const shieldCheckIcon = (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#10B77F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3 }}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
  const emptyAnomalyIcon = (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted, #94a3b8)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3 }}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
  const emptyPredIcon = (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted, #94a3b8)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3 }}>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );

  /* Count badge component */
  const countBadge = (count: number) => (
    <span style={{
      fontSize: 11,
      fontWeight: 700,
      padding: '2px 8px',
      borderRadius: 99,
      background: 'rgba(220,38,38,0.15)',
      color: '#DC2626',
      marginLeft: 4,
      lineHeight: '18px',
      minWidth: 20,
      textAlign: 'center',
      display: 'inline-block',
    }}>
      {count}
    </span>
  );

  /* Prediction icon box colors */
  const predIconColors = ['#3B82F6', '#10B77F', '#F59E0B', '#8B5CF6', '#EC4899', '#F97316'];
  const predIconBox = (idx: number) => {
    const color = predIconColors[idx % predIconColors.length];
    return (
      <div style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: `${color}18`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 10,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
      </div>
    );
  };

  const warningCount = insights.warnings.length;
  /*
   * ⚠️ เดิมหน้านี้ถือว่า 60 ขึ้นไป = เขียว "ระบบปกติ" ส่วนหน้า AI ทุกไซต์ถือว่า
   * ต่ำกว่า 90 คือมีเรื่องให้ดู (ส้ม) — คะแนนเดียวกันแต่สองหน้าให้ความหมาย
   * คนละอย่าง ตอนนี้ใช้เกณฑ์เดียวกันทั้งคู่
   */
  const healthGrad = healthScore >= 90
    ? 'linear-gradient(135deg, #10B77F, #059669)'
    : healthScore >= 60
    ? 'linear-gradient(135deg, #F5A524, #D97706)'
    : 'linear-gradient(135deg, #FF7086, #DC2626)';
  const warnGrad = warningCount > 0
    ? 'linear-gradient(135deg, #FF7086, #DC2626)'
    : 'linear-gradient(135deg, #10B77F, #059669)';

  const elevatedCardStyle: React.CSSProperties = {
    padding: '14px 18px',
    borderRadius: 12,
    background: 'var(--bg-elevated)',
    border: '1px solid var(--glass-border)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    cursor: 'default',
  };

  const elevatedCardHover = {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
  };

  const suggestionBoxStyle: React.CSSProperties = {
    padding: '10px 12px',
    borderRadius: 8,
    background: 'var(--blue-dim, rgba(59,130,246,0.08))',
    borderLeft: '3px solid var(--blue, #3B82F6)',
    fontSize: 12,
    color: '#3B82F6',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    lineHeight: 1.5,
  };

  return (
    <div style={{ padding: 0 }}>
      {/* Page title removed */}

      {/* Top gradient stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
        <StatCard
          icon={heartIcon}
          label={t('healthScore', lang)}
          value={<>{healthScore}<span style={{ fontSize: 17, fontWeight: 600 }}>%</span></>}
          sub={healthScore >= 90 ? t('systemOK', lang) : t('needCheck', lang)}
          gradient={healthGrad}
        />
        <StatCard
          icon={alertIcon}
          label={t('warnings', lang)}
          value={warningCount}
          sub={t('activeWarnings', lang) || 'Active Warnings'}
          gradient={warnGrad}
        />
        {/* ไซต์เครื่องมือแพทย์ไม่มีการคาดการณ์ — การคาดการณ์ที่ระบบมีเป็นของโดเมนอื่น
            (โซ่ความเย็น · ค่าไฟโรงบำบัด) ซึ่งไม่มีความหมายกับห้อง MRI/CT
            ตัวเลขที่ไม่มีความหมายทำให้คนเลิกอ่านทั้งหน้า จึงตัดออกทั้งการ์ด */}
        {!isMedical && <StatCard
          icon={predIcon}
          label={t('predictions', lang)}
          value={insights.predictions.length}
          sub={t('forecasts', lang) || 'Forecasts'}
          gradient="linear-gradient(135deg, #4C8BF5, #2563EB)"
        />}
      </div>

      {/* How-to-read guide / legend */}
      <div style={panelStyle}>
        <div
          style={{ ...headStyle, cursor: 'pointer', justifyContent: 'space-between' }}
          onClick={() => setGuideOpen((o) => !o)}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent, #F5A623)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            {lang === 'th' ? 'คู่มือการอ่านผล AI' : 'How to read AI insights'}
          </span>
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: guideOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
        {guideOpen && (
          <div style={bodyStyle}>
            {/* Safe band callout */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10,
              background: 'var(--blue-dim, rgba(59,130,246,0.08))', borderLeft: '3px solid #3B82F6',
              fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0z" />
              </svg>
              <span>
                {isSupply
                  ? (lang === 'th'
                    ? <>ระบบเฝ้าระวัง <b style={{ color: 'var(--text-primary)' }}>ปั๊มน้ำ</b> และ <b style={{ color: 'var(--text-primary)' }}>ระดับน้ำในถัง</b> — แจ้งเตือนเมื่อปั๊มโอเวอร์โหลด/ออฟไลน์ หรือระดับน้ำต่ำกว่าเกณฑ์ (เตือน &lt; 20% · วิกฤต &lt; 10% ของความสูงถัง)</>
                    : <>Monitors <b style={{ color: 'var(--text-primary)' }}>water pumps</b> and <b style={{ color: 'var(--text-primary)' }}>tank levels</b> — alerts on pump overload/offline or low level (warn &lt; 20%, critical &lt; 10% of tank height).</>)
                  : isWater
                  ? (lang === 'th'
                    ? <>เกณฑ์มาตรฐานน้ำทิ้ง (อุตสาหกรรม): <b style={{ color: 'var(--text-primary)' }}>BOD ≤ 20</b> · <b style={{ color: 'var(--text-primary)' }}>COD ≤ 120</b> · <b style={{ color: 'var(--text-primary)' }}>pH 5.5–9</b> mg/L — หากเกินช่วงนี้ระบบจะแจ้งเตือนทันที (ปรับค่าได้ที่ตั้งค่าไซต์)</>
                    : <>Effluent discharge standard: <b style={{ color: 'var(--text-primary)' }}>BOD ≤ 20</b> · <b style={{ color: 'var(--text-primary)' }}>COD ≤ 120</b> · <b style={{ color: 'var(--text-primary)' }}>pH 5.5–9</b> mg/L. Breaching these triggers an alert (configurable in Site Settings).</>)
                  : isEnergy
                  ? (lang === 'th'
                    ? <>เกณฑ์คุณภาพไฟที่ใช้เฝ้าระวัง: <b style={{ color: 'var(--text-primary)' }}>PF ≥ 0.85</b> (ต่ำกว่านี้การไฟฟ้าคิดค่าปรับ kVAr) · <b style={{ color: 'var(--text-primary)' }}>ความไม่สมดุล ≤ 2%</b> · <b style={{ color: 'var(--text-primary)' }}>THD กระแส ≤ 8% · แรงดัน ≤ 5%</b></>
                    : <>Power-quality limits watched here: <b style={{ color: 'var(--text-primary)' }}>PF ≥ 0.85</b> (below it the utility charges a kVAr penalty) · <b style={{ color: 'var(--text-primary)' }}>unbalance ≤ 2%</b> · <b style={{ color: 'var(--text-primary)' }}>THD current ≤ 8%, voltage ≤ 5%</b></>)
                  : isMedical
                  ? (lang === 'th'
                    ? <>เกณฑ์ห้องเครื่องมือแพทย์ที่ใช้เป็นค่าตั้งต้น: <b style={{ color: 'var(--text-primary)' }}>{ROOM.tMin}–{ROOM.tMax}°C</b> · <b style={{ color: 'var(--text-primary)' }}>{ROOM.hMin}–{ROOM.hMax}%RH</b> — เป็นช่วงที่ใช้กันทั่วไป ไม่ใช่สเปกของผู้ผลิต หากตั้งเกณฑ์เฉพาะของเครื่องไว้ ระบบจะยึดค่านั้นก่อน</>
                    : <>Default band for an equipment room: <b style={{ color: 'var(--text-primary)' }}>{ROOM.tMin}–{ROOM.tMax}°C</b> · <b style={{ color: 'var(--text-primary)' }}>{ROOM.hMin}–{ROOM.hMax}%RH</b> — a common range, not a manufacturer specification. A per-device limit always takes precedence.</>)
                  : isGenset
                  ? (lang === 'th'
                    ? <>คะแนนความพร้อมของเครื่องกำเนิดไฟฟ้าคิดจากสัญญาณที่ชุดควบคุมส่งมาจริง: <b style={{ color: 'var(--text-primary)' }}>สัญญาณเตือน</b> · <b style={{ color: 'var(--text-primary)' }}>แบตเตอรี่สตาร์ท (ระบบ 24V ปกติ 26–28 V)</b> · <b style={{ color: 'var(--text-primary)' }}>น้ำหล่อเย็น</b> · <b style={{ color: 'var(--text-primary)' }}>โหมดควบคุม</b> — เครื่องที่จอดรออยู่ขณะไฟหลักปกติ คือสภาพที่ถูกต้อง ไม่ใช่ความผิดปกติ</>
                    : <>The generator readiness score comes from what the controller actually reports: <b style={{ color: 'var(--text-primary)' }}>alarms</b> · <b style={{ color: 'var(--text-primary)' }}>start battery (26–28 V on a 24V system)</b> · <b style={{ color: 'var(--text-primary)' }}>coolant</b> · <b style={{ color: 'var(--text-primary)' }}>control mode</b>. A set sitting idle while mains is healthy is the correct state, not a fault.</>)
                  : isPoultry
                  ? (lang === 'th'
                    ? <>โรงเรือนไก่เนื้อตัดสินด้วย <b style={{ color: 'var(--text-primary)' }}>เกณฑ์อุณหภูมิตามอายุไก่</b> (ลูกไก่ ~33°C ลดลงจน ~24°C ที่ 5 สัปดาห์ ±2°C — ต้องบันทึกวันลงไก่) และ <b style={{ color: 'var(--text-primary)' }}>ดัชนีความร้อน °F+%RH</b> (160 เฝ้าระวัง · 165 เครียด · 170 อันตราย) — เซนเซอร์ที่ไม่ส่งข้อมูลเกิน 15 นาทีนับว่าเงียบ เพราะในโรงระบบปิดอาจหมายถึงไฟดับ</>
                    : <>Broiler houses are judged by an <b style={{ color: 'var(--text-primary)' }}>age-based temperature band</b> (~33°C for chicks down to ~24°C at 5 weeks, ±2°C — needs the placement date) and the <b style={{ color: 'var(--text-primary)' }}>heat index °F+%RH</b> (160 watch · 165 stress · 170 danger). A sensor silent for 15+ min counts as down — in a closed house that may be a power cut.</>)
                  : isCold
                  ? (lang === 'th'
                    ? <>ช่วงอุณหภูมิปลอดภัยของวัคซีน คือ <b style={{ color: 'var(--text-primary)' }}>2–8°C</b> (มาตรฐาน WHO EPI) — หากออกนอกช่วงนี้ วัคซีนเสี่ยงเสื่อมสภาพหรือแข็งตัว</>
                    : <>The vaccine-safe temperature range is <b style={{ color: 'var(--text-primary)' }}>2–8°C</b> (WHO EPI). Outside this range vaccines risk heat damage or freezing.</>)
                  : (lang === 'th'
                    ? <>ไซต์นี้ใช้ <b style={{ color: 'var(--text-primary)' }}>เกณฑ์ที่ตั้งไว้กับอุปกรณ์แต่ละตัว</b> เป็นตัวตัดสิน — ระบบไม่สมมติช่วงค่าปกติให้เอง ค่าไหนยังไม่ได้ตั้งเกณฑ์ก็จะไม่ถูกเตือน ตั้งได้ที่หน้า <b style={{ color: 'var(--text-primary)' }}>ตั้งค่าอุปกรณ์</b></>
                    : <>This site is judged against <b style={{ color: 'var(--text-primary)' }}>the thresholds configured on each device</b> — no range is assumed for you. A field with no threshold set is not alerted on; set them in <b style={{ color: 'var(--text-primary)' }}>Device Settings</b>.</>)}
              </span>
            </div>

            {/* Severity legend */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
              {lang === 'th' ? 'ระดับการแจ้งเตือน' : 'Alert levels'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 18 }}>
              {(isSupply
                ? [
                  { c: '#DC2626', l: 'CRITICAL', th: 'ปั๊มโอเวอร์โหลด/ตัดการทำงาน, อุปกรณ์ออฟไลน์ หรือระดับน้ำต่ำมาก (< 10%) — ต้องแก้ไขทันที', en: 'Pump overload/trip, device offline, or very low tank level (<10%) — act immediately' },
                  { c: '#F59E0B', l: 'WARNING', th: 'ระดับน้ำในถังต่ำ (< 20%) — ควรเฝ้าระวังและเตรียมเติมน้ำ', en: 'Low tank level (<20%) — monitor and prepare to refill' },
                  { c: '#3B82F6', l: 'INFO', th: 'ข้อมูลทั่วไป เช่น รออุปกรณ์ส่งค่าจริงจาก Gateway', en: 'General info, e.g. awaiting live data from the Gateway' },
                ]
                : isWater
                ? [
                  { c: '#DC2626', l: 'CRITICAL', th: 'ปั๊มโอเวอร์โหลด/ตัดการทำงาน, อุปกรณ์ออฟไลน์ หรือ pH น้ำทิ้งเกินมาตรฐาน — ต้องแก้ไขทันที', en: 'Pump overload/trip, device offline, or effluent pH out of range — act immediately' },
                  { c: '#F59E0B', l: 'WARNING', th: 'COD/BOD น้ำทิ้งเกินเกณฑ์ หรือ DO ในถังเติมอากาศต่ำ — ควรเฝ้าระวังและปรับกระบวนการ', en: 'Effluent COD/BOD over limit or low DO in aeration tank — monitor & adjust the process' },
                  { c: '#3B82F6', l: 'INFO', th: 'ข้อมูลทั่วไป เช่น รออุปกรณ์ส่งค่าจริงจาก Gateway', en: 'General info, e.g. awaiting live data from the Gateway' },
                ]
                : isEnergy
                ? [
                  { c: '#DC2626', l: 'CRITICAL', th: 'มิเตอร์ออฟไลน์, PF ต่ำกว่า 0.80 หรือความไม่สมดุลเกิน 6% — ต้องแก้ไขทันที', en: 'Meter offline, PF below 0.80, or unbalance over 6% — act immediately' },
                  { c: '#F59E0B', l: 'WARNING', th: 'PF ต่ำกว่า 0.85 (เสี่ยงค่าปรับ kVAr), ความไม่สมดุลเกิน 2% หรือ THD เกินเกณฑ์', en: 'PF below 0.85 (kVAr penalty risk), unbalance over 2%, or THD over limit' },
                  { c: '#3B82F6', l: 'INFO', th: 'ข้อมูลทั่วไป เช่น รออุปกรณ์ส่งค่าจริงจาก Gateway', en: 'General info, e.g. awaiting live data from the Gateway' },
                ]
                : isMedical
                ? [
                  { c: '#DC2626', l: 'CRITICAL', th: `อุปกรณ์ไม่ส่งข้อมูล · ห้องร้อนเกินเกณฑ์เกิน 2°C (เกิน ${ROOM.tMax + 2}°C) · ความชื้นเกิน 70%RH`, en: `Device not reporting · room more than 2°C over the limit (above ${ROOM.tMax + 2}°C) · humidity above 70%RH` },
                  { c: '#F59E0B', l: 'WARNING', th: `ห้องเกินเกณฑ์ไม่เกิน 2°C (${ROOM.tMax}–${ROOM.tMax + 2}°C) · ความชื้น ${ROOM.hMax}–70%RH`, en: `Room up to 2°C over the limit (${ROOM.tMax}–${ROOM.tMax + 2}°C) · humidity ${ROOM.hMax}–70%RH` },
                  { c: '#3B82F6', l: 'INFO', th: `ห้องเย็นกว่า ${ROOM.tMin}°C หรือความชื้นต่ำกว่า ${ROOM.hMin}%RH — ไม่หักคะแนน`, en: `Room below ${ROOM.tMin}°C or humidity below ${ROOM.hMin}%RH — no score deduction` },
                ]
                : isGenset
                ? [
                  { c: '#DC2626', l: 'CRITICAL', th: 'ชุดควบคุมมีสัญญาณเตือน · แบตเตอรี่ต่ำกว่า 24V · ไฟหลักดับแต่เครื่องไม่เดิน — ต้องแก้ไขทันที', en: 'Controller alarm · start battery below 24V · mains down but the set has not started — act immediately' },
                  { c: '#F59E0B', l: 'WARNING', th: 'แบตเตอรี่ค่อนข้างต่ำ · อยู่ในโหมด Manual (ไฟดับแล้วต้องมีคนไปกด) · เตือนน้ำมันต่ำ', en: 'Battery getting low · in Manual mode (someone must start it) · low-fuel alarm' },
                  { c: '#3B82F6', l: 'INFO', th: 'ข้อมูลทั่วไป เช่น ค่าที่ชุดควบคุมไม่ได้ส่งมา — ไม่หักคะแนน', en: 'General info, e.g. a value the controller does not report — no score deduction' },
                ]
                : isCold
                ? [
                  { c: '#DC2626', l: 'CRITICAL', th: 'อุณหภูมิเกิน 8°C / ต่ำกว่า 2°C หรือไฟดับ — ต้องแก้ไขทันที', en: 'Over 8°C / below 2°C or power loss — act immediately' },
                  { c: '#F59E0B', l: 'WARNING', th: 'ใกล้ขีดเกณฑ์ (≥7°C หรือ ≤3°C) — ควรเฝ้าระวัง', en: 'Near the limit (≥7°C or ≤3°C) — keep watch' },
                  { c: '#3B82F6', l: 'INFO', th: 'ข้อมูลทั่วไป เช่น ความชื้นสูง', en: 'General info, e.g. high humidity' },
                ]
                : [
                  { c: '#DC2626', l: 'CRITICAL', th: 'อุปกรณ์ออฟไลน์ หรือค่าที่ตั้งเตือนแบบ Alarm ไว้เกินเกณฑ์ — ต้องแก้ไขทันที', en: 'Device offline, or a field configured as an alarm has breached its limit — act immediately' },
                  { c: '#F59E0B', l: 'WARNING', th: 'ค่าเกินเกณฑ์ที่ตั้งไว้กับอุปกรณ์ (สูงหรือต่ำกว่าที่กำหนด)', en: 'A value is outside the threshold configured on the device (above or below)' },
                  { c: '#3B82F6', l: 'INFO', th: 'ข้อมูลทั่วไป เช่น รออุปกรณ์ส่งค่าจริงจาก Gateway', en: 'General info, e.g. awaiting live data from the Gateway' },
                ]
              ).map((s) => (
                <div key={s.l} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 10, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.c, flexShrink: 0, marginTop: 4, boxShadow: `0 0 8px ${s.c}66` }} />
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: s.c, letterSpacing: '.04em' }}>{s.l}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45, marginTop: 2 }}>{lang === 'th' ? s.th : s.en}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Section meanings */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
              {lang === 'th' ? 'ความหมายของแต่ละส่วน' : 'What each section means'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
              {[
                {
                  t: lang === 'th' ? 'Health Score (คะแนนสุขภาพ)' : 'Health Score',
                  d: lang === 'th' ? 'คะแนนรวมของไซต์ 0–100% ยิ่งสูงยิ่งดี (หักคะแนนตามจำนวน critical/warning)' : 'Overall site score 0–100%, higher is better (deductions per critical/warning).',
                },
                {
                  t: lang === 'th' ? 'Warnings (การแจ้งเตือน)' : 'Warnings',
                  d: lang === 'th' ? 'เหตุการณ์ที่เกินเกณฑ์ ณ ขณะนี้ พร้อมคำแนะนำการแก้ไข' : 'Threshold breaches happening right now, with a suggested action.',
                },
                // ไซต์เครื่องมือแพทย์ไม่มีการคาดการณ์ จึงไม่ต้องอธิบายว่ามันคืออะไร
                ...(isMedical ? [] : [{
                  t: lang === 'th' ? 'Predictions (การคาดการณ์)' : 'Predictions',
                  d: isSupply
                    ? (lang === 'th' ? 'แนวโน้มล่วงหน้า ได้แก่ ระดับน้ำในถังแต่ละถัง (% ของความสูงเต็ม)' : 'Forward-looking signals: water level in each tank (% of full height).')
                    : isWater
                    ? (lang === 'th' ? 'แนวโน้มล่วงหน้า ได้แก่ คุณภาพน้ำทิ้ง (ได้มาตรฐาน/เกินเกณฑ์) และค่าไฟฟ้าเดือนนี้โดยประมาณ' : 'Forward-looking signals: effluent quality (pass/over-limit) and estimated monthly electricity cost.')
                    : isEnergy
                    ? (lang === 'th' ? 'สรุปสถานะคุณภาพไฟ ได้แก่ สัดส่วนมิเตอร์ที่ PF ผ่านเกณฑ์ และระยะห่างจากเกณฑ์ของค่าที่ใกล้ชนที่สุด' : 'Power-quality status: share of meters with PF within limit, and how close the nearest measure is to its limit.')
                    : isGenset
                    ? (lang === 'th' ? 'คะแนนความพร้อมของเครื่องกำเนิดไฟฟ้า พร้อมรายการที่หักคะแนน และค่าไฟเดือนนี้โดยประมาณถ้ามีมิเตอร์' : 'Generator readiness score with the items that deducted from it, plus estimated monthly electricity cost when a meter is present.')
                    : isCold
                    ? (lang === 'th' ? 'แนวโน้มล่วงหน้า ได้แก่ % ความสอดคล้องโซ่ความเย็น, เวลาที่จะถึง 8°C และการแจ้งบำรุงรักษา' : 'Forward-looking signals: cold-chain compliance %, time-to-8°C, and maintenance reminders.')
                    : (lang === 'th' ? 'ตัวเลขที่คำนวณจากข้อมูลจริงของไซต์นี้ เช่น ค่าไฟเดือนนี้โดยประมาณจากมิเตอร์ที่ติดตั้งอยู่' : 'Figures computed from this site’s own data, e.g. the projected monthly electricity cost from its installed meters.'),
                }]),
              ].map((s) => (
                <div key={s.t} style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{s.t}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{s.d}</div>
                </div>
              ))}
            </div>

            {/* Predictions — detailed breakdown of each case
                ไซต์เครื่องมือแพทย์ไม่มีการคาดการณ์ จึงไม่มีอะไรให้อธิบาย และคำอธิบาย
                ที่เหลืออยู่เป็นของตู้แช่วัคซีนล้วน ๆ (2–8°C · คอมเพรสเซอร์ · ซีลประตู)
                ซึ่งอ่านแล้วทำให้เข้าใจผิดว่าระบบกำลังเฝ้าตู้แช่อยู่ */}
            {!isMedical && <>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', margin: '18px 0 8px' }}>
              {lang === 'th' ? 'รายละเอียดการคาดการณ์ (Predictions) แต่ละแบบ' : 'Each prediction explained'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(isSupply ? [
                {
                  c: '#10B77F',
                  t: lang === 'th' ? 'ระดับน้ำในถัง (Tank level)' : 'Tank level',
                  mean: lang === 'th'
                    ? 'ระดับน้ำปัจจุบันของแต่ละถัง คิดเป็น % จากความสูงเต็มของถัง (ถังใต้ดิน / ถังแชมเปญ)'
                    : 'Current level of each tank as % of its full height (ground / elevated tank).',
                  when: lang === 'th'
                    ? 'แสดงเมื่อเซนเซอร์วัดระดับน้ำ (level) ส่งค่าอย่างน้อย 1 ถัง'
                    : 'Shown when at least one water-level sensor reports.',
                },
              ] : isWater ? [
                {
                  c: '#10B77F',
                  t: lang === 'th' ? 'คุณภาพน้ำทิ้ง (Effluent quality)' : 'Effluent quality',
                  mean: lang === 'th'
                    ? 'สรุปว่าน้ำทิ้งได้มาตรฐานหรือไม่ โดยเทียบค่า BOD / COD / pH ล่าสุดกับเกณฑ์ที่ตั้งไว้'
                    : 'Whether the effluent passes standard, comparing the latest BOD / COD / pH against the set limits.',
                  when: lang === 'th'
                    ? 'แสดงเมื่อเซนเซอร์คุณภาพน้ำ (Mtech8_Sensor) ส่งค่าอย่างน้อย 1 ค่า (BOD/COD/pH)'
                    : 'Shown when the water-quality sensor reports at least one of BOD/COD/pH.',
                },
                {
                  c: '#4C8BF5',
                  t: lang === 'th' ? 'ค่าไฟเดือนนี้ (คาดการณ์)' : 'Monthly electricity cost (forecast)',
                  mean: lang === 'th'
                    ? 'ประมาณค่าไฟทั้งเดือนจากพลังงานสะสม 2 โซน (kWh) คูณอัตราค่าไฟ พร้อมกำลังไฟขณะนี้ (kW)'
                    : 'Estimates the monthly bill from the two zones’ accumulated energy (kWh) × tariff, with current load (kW).',
                  when: lang === 'th'
                    ? 'แสดงเมื่อมิเตอร์พลังงานรายงานหน่วยสะสมของเดือน (kWh) มากกว่า 0'
                    : 'Shown when the energy meters report this month’s accumulated kWh above 0.',
                },
              ] : isEnergy ? [
                {
                  c: '#10B77F',
                  t: lang === 'th' ? 'มิเตอร์ที่ PF ผ่านเกณฑ์' : 'Meters within PF limit',
                  mean: lang === 'th'
                    ? 'สัดส่วนมิเตอร์ที่เพาเวอร์แฟกเตอร์ ≥ 0.85 ขณะนี้ เช่น 3/4 ตัว = 75% — ตัวที่ต่ำกว่าเกณฑ์คือจุดที่การไฟฟ้าคิดค่าปรับ kVAr'
                    : 'Share of meters currently at PF ≥ 0.85, e.g. 3 of 4 = 75%. The ones below are where the utility charges a kVAr penalty.',
                  when: lang === 'th'
                    ? 'แสดงเมื่อมีมิเตอร์อย่างน้อย 1 ตัวรายงานค่า PF และกำลังจ่ายโหลดอยู่ (PF = 0 คือไม่มีโหลด ไม่นับ)'
                    : 'Shown when at least one meter reports PF while carrying load (PF = 0 means no load and is not counted).',
                },
                {
                  c: '#F59E0B',
                  t: lang === 'th' ? 'ระยะห่างจากเกณฑ์คุณภาพไฟ' : 'Power-quality headroom',
                  mean: lang === 'th'
                    ? 'ค่าที่ใกล้ชนเกณฑ์ที่สุดในไซต์ คิดเป็น % ของเกณฑ์นั้น (PF 0.85 · ไม่สมดุล 2% · THD กระแส 8% / แรงดัน 5%) — เกิน 100% คือเลยเกณฑ์แล้ว'
                    : 'The measure closest to its limit, as a % of that limit (PF 0.85 · unbalance 2% · THD current 8% / voltage 5%). Over 100% means it has already crossed.',
                  when: lang === 'th'
                    ? 'แสดงเมื่อมิเตอร์รายงานค่าคุณภาพไฟอย่างน้อย 1 ค่า — เป็นค่าปัจจุบัน ไม่ใช่การพยากรณ์ล่วงหน้า'
                    : 'Shown when at least one power-quality register reports. It states the present, not a forecast.',
                },
              ] : isGenset ? [
                {
                  c: '#10B77F',
                  t: lang === 'th' ? 'ความพร้อมของเครื่องกำเนิดไฟฟ้า' : 'Generator readiness',
                  mean: lang === 'th'
                    ? 'คะแนน 0–100 คิดจากกฎตายตัว: สัญญาณเตือน −40 · ไฟหลักดับแต่เครื่องไม่เดิน −40 · แบตเตอรี่ต่ำ −30/−15 · เตือนน้ำมันต่ำ −25 · อยู่ในโหมด Manual −20 — ค่าที่ชุดควบคุมไม่ส่งมาจะไม่ถูกหักคะแนน แต่จะบอกไว้ว่าอ่านไม่ได้'
                    : 'A 0–100 score from fixed rules: controller alarm −40 · mains down with the set not running −40 · low battery −30/−15 · low-fuel alarm −25 · Manual mode −20. Values the controller does not send deduct nothing but are listed as unreadable.',
                  when: lang === 'th'
                    ? 'แสดงเมื่ออุปกรณ์ส่งสัญญาณของชุดควบคุมเครื่องกำเนิดไฟฟ้า — คะแนนชุดเดียวกับที่ใช้ในรายงานประจำวันที่ส่งเข้า LINE'
                    : 'Shown when the device reports generator-controller signals — the same score used in the daily report sent to LINE.',
                },
                {
                  c: '#4C8BF5',
                  t: lang === 'th' ? 'ค่าไฟเดือนนี้ (คาดการณ์)' : 'Monthly electricity cost (forecast)',
                  mean: lang === 'th'
                    ? 'ประมาณค่าไฟทั้งเดือนจากหน่วยสะสมของมิเตอร์ (kWh) คูณอัตราค่าไฟ'
                    : 'Projects the monthly bill from the meters’ accumulated kWh × tariff.',
                  when: lang === 'th'
                    ? 'แสดงเมื่อมีมิเตอร์พลังงานรายงานหน่วยสะสมของเดือนมากกว่า 0'
                    : 'Shown when an energy meter reports this month’s accumulated kWh above 0.',
                },
              ] : isPoultry ? [
                {
                  c: '#10B77F',
                  t: lang === 'th' ? 'รอบเลี้ยงและวันจับโดยประมาณ' : 'Flock day & estimated catch',
                  mean: lang === 'th'
                    ? 'อายุไก่นับจากวันลงไก่ที่บันทึกไว้ และประมาณวันจับที่อายุ 42 วัน (ค่าทั่วไป ไม่ใช่แผนของฟาร์ม)'
                    : 'Bird age from the recorded placement date, with a catch date estimated at 42 days (a general figure, not the farm plan).',
                  when: lang === 'th' ? 'แสดงเมื่อโรงเรือนมีรอบเลี้ยงที่ยังไม่ปิด' : 'Shown for houses with an open flock.',
                },
                {
                  c: '#4C8BF5',
                  t: lang === 'th' ? 'เมื่อวาน (ทั้งฟาร์ม)' : 'Yesterday (whole farm)',
                  mean: lang === 'th'
                    ? 'สัดส่วนชั่วโมงที่อยู่ในเกณฑ์ตามอายุไก่ และชั่วโมงที่ดัชนีความร้อนถึงระดับเครียด จากค่าเฉลี่ยรายชั่วโมงของเมื่อวาน'
                    : 'Share of hours inside the age band, and hours at heat-stress level, from yesterday’s hourly averages.',
                  when: lang === 'th' ? 'แสดงเมื่อเมื่อวานมีข้อมูล — % ในเกณฑ์ขึ้นเฉพาะโรงที่มีเกณฑ์ให้เทียบ' : 'Shown when yesterday has data — % in band only for houses that have a band.',
                },
              ] : !isCold ? [
                {
                  c: '#4C8BF5',
                  t: lang === 'th' ? 'ค่าไฟเดือนนี้ (คาดการณ์)' : 'Monthly electricity cost (forecast)',
                  mean: lang === 'th'
                    ? 'ประมาณค่าไฟทั้งเดือนจากหน่วยสะสมของมิเตอร์ (kWh) คูณอัตราค่าไฟ พร้อมกำลังไฟขณะนี้ (kW)'
                    : 'Projects the monthly bill from the meters’ accumulated kWh × tariff, with the current load (kW).',
                  when: lang === 'th'
                    ? 'แสดงเมื่อมีมิเตอร์พลังงานรายงานหน่วยสะสมของเดือนมากกว่า 0 — นับเฉพาะมิเตอร์หลักของแต่ละ Gateway เพื่อไม่ให้มิเตอร์ย่อยถูกนับซ้ำ'
                    : 'Shown when an energy meter reports this month’s accumulated kWh above 0 — only each gateway’s main meter counts, so sub-meters are not double-counted.',
                },
              ] : [
                {
                  c: '#10B77F',
                  t: lang === 'th' ? 'ความสอดคล้องโซ่ความเย็น (Compliance %)' : 'Cold-chain compliance (%)',
                  mean: lang === 'th'
                    ? 'สัดส่วนของจุดวัดอุณหภูมิที่อยู่ในช่วงปลอดภัย 2–8°C ณ ขณะนี้ เช่น 3/4 จุด = 75%'
                    : 'Share of temperature points currently within the 2–8°C safe range, e.g. 3 of 4 points = 75%.',
                  when: lang === 'th'
                    ? 'แสดงเสมอเมื่อมีจุดวัดอุณหภูมิอย่างน้อย 1 จุด — ใช้ดูภาพรวมความปลอดภัยทั้งไซต์อย่างรวดเร็ว'
                    : 'Always shown when there is at least one temperature point — a quick site-wide safety overview.',
                },
                {
                  c: '#F59E0B',
                  t: lang === 'th' ? 'คาดการณ์อุณหภูมิเกินเกณฑ์ (เวลาถึง 8°C)' : 'Time to exceed limit (to 8°C)',
                  mean: lang === 'th'
                    ? 'ประมาณการว่าตู้แช่ที่ร้อนสุดจะใช้เวลาอีกกี่ชั่วโมงจึงจะถึง 8°C หากแนวโน้มความร้อนยังคงเดิม'
                    : 'Estimated hours until the warmest fridge reaches 8°C if the warming trend continues.',
                  when: lang === 'th'
                    ? 'แสดงเมื่อตู้ที่ร้อนสุดมีอุณหภูมิอยู่ในช่วง 5–8°C (ยังปลอดภัยแต่กำลังไต่ขึ้น) — เพื่อให้เตรียมรับมือก่อนเกินจริง'
                    : 'Shown when the warmest fridge is 5–8°C (safe but rising) — so staff can act before an actual excursion.',
                },
                {
                  c: '#8B5CF6',
                  t: lang === 'th' ? 'ควรตรวจบำรุงรักษา (Maintenance)' : 'Maintenance reminder',
                  mean: lang === 'th'
                    ? 'คำแนะนำให้เข้าตรวจคอมเพรสเซอร์/ซีลประตู เมื่อตู้แช่ทำงานอยู่ในช่วงอุณหภูมิสูงต่อเนื่อง'
                    : 'A reminder to service the compressor / door seal when a fridge runs warm for a sustained period.',
                  when: lang === 'th'
                    ? 'แสดงเมื่อตู้ที่ร้อนสุดมีอุณหภูมิ ≥7°C — เป็นสัญญาณว่าการทำความเย็นอาจเริ่มเสื่อม'
                    : 'Shown when the warmest fridge is ≥7°C — a sign cooling performance may be degrading.',
                },
              ]).map((p) => (
                <div key={p.t} style={{ display: 'flex', gap: 10, padding: '12px 14px', borderRadius: 10, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)', borderLeft: `3px solid ${p.c}` }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: `${p.c}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={p.c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
                    </svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>{p.t}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{p.mean}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5, marginTop: 5, display: 'flex', gap: 5 }}>
                      <span style={{ fontWeight: 700, color: p.c, flexShrink: 0 }}>{lang === 'th' ? 'แสดงเมื่อ:' : 'Shown when:'}</span>
                      <span>{p.when}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Confidence note */}
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 14, lineHeight: 1.5 }}>
              {lang === 'th'
                ? '※ Confidence = ระดับความมั่นใจของ AI ต่อผลนั้น ๆ (ยิ่งสูงยิ่งน่าเชื่อถือ) — แถบสีเขียว ≥80%, เหลือง 50–79%, แดง <50%'
                : '※ Confidence = how sure the AI is about a result (higher is more reliable) — green ≥80%, amber 50–79%, red <50%.'}
            </div>
            </>}
          </div>
        )}
      </div>

      {/* Warnings section */}
      <div style={panelStyle}>
        <div style={headStyle}>
          {warningTriangleIcon}
          {t('warnings', lang)}
          {warningCount > 0 && countBadge(warningCount)}
        </div>
        <div style={bodyStyle}>
          {insights.warnings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              {shieldCheckIcon}
              <span style={{ color: '#10B77F', fontSize: 13, fontWeight: 600 }}>
                {t('noWarnings', lang)}
              </span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {insights.warnings.map((w, idx) => (
                <div
                  key={idx}
                  style={elevatedCardStyle}
                  onMouseEnter={(e) => {
                    Object.assign(e.currentTarget.style, elevatedCardHover);
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  {/* Title row: dot + badge + title */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: sevDotColor(w.sv),
                        flexShrink: 0,
                        boxShadow: `0 0 8px ${sevDotColor(w.sv)}66`,
                      }}
                    />
                    {sevBadge(w.sv)}
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
                      {w.title}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 8 }}>
                    {w.desc}
                  </div>
                  {/* Device S/N as monospace badge */}
                  <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 11,
                      fontFamily: 'monospace',
                      padding: '3px 8px',
                      borderRadius: 6,
                      background: 'var(--bg-surface, rgba(0,0,0,0.04))',
                      border: '1px solid var(--glass-border)',
                      color: 'var(--text-muted)',
                    }}>
                      {circuitIcon}
                      S/N: {w.device}
                    </span>
                  </div>
                  {/* Suggestion in tinted blue box */}
                  <div style={suggestionBoxStyle}>
                    <span style={{ flexShrink: 0, marginTop: 1 }}>{lightbulbIcon}</span>
                    <span>{t('suggest', lang)}: {w.action}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Predictions section */}
      {!isMedical && <div style={panelStyle}>
        <div style={headStyle}>
          {trendChartIcon}
          {t('predictions', lang)}
          {insights.predictions.length > 0 && countBadge(insights.predictions.length)}
        </div>
        <div style={bodyStyle}>
          {insights.predictions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              {emptyPredIcon}
              <span style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 600 }}>
                {t('noPredictions', lang)}
              </span>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {insights.predictions.map((p, idx) => (
                <div
                  key={idx}
                  style={{
                    ...elevatedCardStyle,
                    padding: 16,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  onMouseEnter={(e) => {
                    Object.assign(e.currentTarget.style, elevatedCardHover);
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  {predIconBox(idx)}
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
                    {p.title}
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--gold, #F5A623)', letterSpacing: '-0.03em', marginBottom: 6 }}>
                    {p.value}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12, flex: 1 }}>
                    {p.desc}
                  </div>
                  {/* Confidence progress bar */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('confidence', lang)}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>{p.confidence}%</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-surface, #f1f5f9)', overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${p.confidence}%`,
                          borderRadius: 3,
                          background: p.confidence >= 80
                            ? 'linear-gradient(90deg, #10B77F, #34D399)'
                            : p.confidence >= 50
                              ? 'linear-gradient(90deg, #FBBF24, #F59E0B)'
                              : 'linear-gradient(90deg, #F87171, #DC2626)',
                          transition: 'width .3s ease',
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>}
    </div>
  );
}
